import React, { useState } from 'react';
import {
  Alert,
  Box,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ReplayIcon from '@mui/icons-material/Replay';
import { useLocation, useNavigate } from 'react-router-dom';

import logo from '../assets/logo_no_bg.png';
import { resendVerification } from '../api/auth';
import { normalizeUserFacingError } from '../utils/normalizeUserFacingError';
import ActionButton from '../components/common/ActionButton';

type NoticeState = {
  email?: string;
  devVerificationUrl?: string;
};

export default function VerifyEmailNoticePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state || {}) as NoticeState;
  const email = state.email || '';
  const [devVerificationUrl, setDevVerificationUrl] = useState(
    state.devVerificationUrl || ''
  );
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleResend = async () => {
    if (!email.trim()) {
      setError('Return to sign in and request a new verification email from there.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await resendVerification(email.trim());
      setDevVerificationUrl(response.dev_verification_url || '');
      setMessage('A new verification email was sent if this account is still unverified.');
    } catch (resendError: any) {
      setError(
        normalizeUserFacingError(
          resendError,
          'Unable to resend verification email. Please try again.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        background: 'linear-gradient(135deg, #0A0E14 0%, #13171F 100%)',
      }}
    >
      <Paper
        elevation={24}
        sx={{
          width: '100%',
          maxWidth: 500,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          background: 'rgba(19, 23, 31, 0.95)',
          border: '1px solid rgba(57, 255, 20, 0.22)',
        }}
      >
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ width: 52, height: 52 }}>
              <img
                src={logo}
                alt="Quantik Mind logo"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Check your email
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Account created. Verify your email before continuing with onboarding.
              </Typography>
            </Box>
          </Stack>

          <Alert severity="info" icon={<MarkEmailReadIcon />}>
            We sent a verification link{email ? ` to ${email}` : ''}. Verify your
            email before signing in. The link expires after 24 hours.
          </Alert>

          {message && <Alert severity="success">{message}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          {devVerificationUrl && (
            <Alert severity="warning">
              Development verification link:{' '}
              <Link href={devVerificationUrl} color="inherit">
                open verification
              </Link>
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <ActionButton
              onClick={() => navigate('/login')}
            >
              Back to sign in
            </ActionButton>
            <ActionButton
              startIcon={<ReplayIcon />}
              onClick={() => void handleResend()}
              disabled={loading || !email.trim()}
            >
              {loading ? 'Sending...' : 'Send another verification email'}
            </ActionButton>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
